import React, { useEffect, useState } from "react";
import { Row, Col, Card, Accordion, ProgressBar } from "react-bootstrap";
import { FormattedMessage, useIntl } from "react-intl";
import Loading from "../../../../../components/general/Loading";
import {
  handleError,
  isNotBusinessError,
} from "../../../../../utils/handleError";
import {
  getIndicatorGenderCounter,
  getIndicatorSchoolQualificationsCounter,
  getIndicatorQualificationLevelsCounter,
  getIndicatorAgeCounter,
} from "../../../../../rest/indicator";
import ExportDialog from "../utils/ExportDialog";
import LastUpdate from "../utils/LastUpdate";
import * as XLSX from "xlsx";
import { buildWorkbookRepresentative } from "../sectionsIndicatorsCandidatures/exporters/exporterIndicatorRepresentative";
import { useClientPdf } from "../utils/useClientPdf";

export default function IndicatorRepresentativeData() {
  const intl = useIntl();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [genders, setGenders] = useState([]);
  const [schools, setSchools] = useState([]);
  const [qualifications, setQualifications] = useState([]);
  const [ages, setAges] = useState(null);

  const [showExport, setShowExport] = useState(false);

  const [lastUpdate, setLastUpdate] = useState(null);
  const [refreshVersion, setRefreshVersion] = useState(null);

  const { pdfRef, exportPdf } = useClientPdf({
    marginMm: 10,
    orientation: "portrait",
    hideSelectors: ['[data-hide-on-export="true"]', ".modal", ".toast"],
  });

  const pickLatestMeta = (metas) => {
    const withTs = metas
      .filter((m) => !!m?.ts)
      .map((m) => ({ ...m, epoch: Date.parse(m.ts) }))
      .filter((m) => !Number.isNaN(m.epoch));
    if (withTs.length === 0) return { ts: null, ver: null };
    withTs.sort((a, b) => b.epoch - a.epoch);
    return { ts: withTs[0].ts, ver: withTs[0].ver ?? null };
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [respGender, respSchool, respQual, respAge] = await Promise.all([
          getIndicatorGenderCounter(),
          getIndicatorSchoolQualificationsCounter(),
          getIndicatorQualificationLevelsCounter(),
          getIndicatorAgeCounter(),
        ]);

        const genderPayload = respGender?.data ?? {};
        const schoolPayload = respSchool?.data ?? {};
        const qualPayload = respQual?.data ?? {};
        const agePayload = respAge?.data ?? {};

        setGenders(genderPayload.data || []);
        setSchools(schoolPayload.data || []);
        setQualifications(qualPayload.data || []);
        setAges((agePayload.data && agePayload.data[0]) || null);
        const { ts, ver } = pickLatestMeta([
          {
            ts: genderPayload.lastRefreshedAt,
            ver: genderPayload.refreshVersion,
          },
          {
            ts: schoolPayload.lastRefreshedAt,
            ver: schoolPayload.refreshVersion,
          },
          { ts: qualPayload.lastRefreshedAt, ver: qualPayload.refreshVersion },
          { ts: agePayload.lastRefreshedAt, ver: agePayload.refreshVersion },
        ]);
        setLastUpdate(ts || null);
        setRefreshVersion(ver || null);
      } catch (err) {
        if (isNotBusinessError(err)) setError(err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  if (loading) return <Loading />;
  if (error) return handleError(error);

  const sumBy = (arr, key = "counter") =>
    Array.isArray(arr) ? arr.reduce((s, i) => s + (i?.[key] || 0), 0) : 0;
  const toPct = (v, t) => (t > 0 ? Math.round((v / t) * 100) : 0);

  const genderMsgFor = (code) => {
    if (code === "-" || code === "" || code == null)
      return { id: "indicator.genderForm.unknown", defaultMessage: "Sem informação" };
    const c = Number(code);
    if (c === 0)
      return { id: "indicator.genderForm.M", defaultMessage: "Masculino" };
    if (c === 1)
      return { id: "indicator.genderForm.F", defaultMessage: "Feminino" };
    return { id: `promoterForm.gender.${code}`, defaultMessage: String(code) };
  };

  const agesArray = ages
    ? [
        { key: "15-24", label: "15-24", counter: ages.from15To24 || 0 },
        { key: "25-34", label: "25-34", counter: ages.from25To34 || 0 },
        { key: "35-44", label: "35-44", counter: ages.from35To44 || 0 },
        { key: "45-54", label: "45-54", counter: ages.from45To54 || 0 },
        { key: "55-64", label: "55-64", counter: ages.from55To64 || 0 },
        { key: "65+", label: "> 65", counter: ages.more65 || 0 },
        { key: "unknown", label: "Sem informação", counter: ages.unknown || 0 },
      ].filter((item) => item.key !== "unknown" || item.counter > 0)
    : [];

  const totalAges = agesArray.reduce((s, i) => s + i.counter, 0);

  const gendersView = genders
    .map((g) => ({
      key: g.gender,
      label: <FormattedMessage {...genderMsgFor(g.gender)} />,
      counter: g.counter || 0,
    }))
    .sort((a, b) => b.counter - a.counter);

  const schoolsView = schools
    .map((s) => ({
      key: s.code,
      label: (
        <FormattedMessage
          id={`schoolQualification.${s.code}`}
          defaultMessage={s.code}
        />
      ),
      counter: s.counter || 0,
    }))
    .sort((a, b) => b.counter - a.counter);

  const qualificationsView = qualifications
    .map((q) => ({
      key: q.code ?? "-",
      label: q.code ?? "-",
      counter: q.counter || 0,
    }))
    .sort((a, b) => b.counter - a.counter);

  const handleExportExcelFinal = async () => {
    try {
      const wb = await buildWorkbookRepresentative();
      if (!wb || !wb.SheetNames) throw new Error("Workbook vazio");
      XLSX.writeFile(wb, "indicadores_representativos.xlsx");
      setShowExport(false);
    } catch (e) {
      console.error("[Excel Representativos] falhou:", e);
      alert("Falha ao gerar Excel: " + (e?.message || e));
    }
  };

  const SectionHeader = ({ title, right }) => (
    <div className="d-flex justify-content-between align-items-center mb-3">
      <h6 className="text-secondary mb-0">{title}</h6>
      {right && <div className="text-muted">{right}</div>}
    </div>
  );

  const BarRow = ({ labelLeft, value, total }) => {
    const pct = toPct(value, total);
    return (
      <div className="mb-2">
        <div className="d-flex justify-content-between">
          <div
            className="text-truncate"
            title={typeof labelLeft === "string" ? labelLeft : undefined}
          >
            {labelLeft}
          </div>
          <div className="ms-2">
            <strong>{value}</strong>{" "}
            <span className="text-muted">({pct}%)</span>
          </div>
        </div>
        <ProgressBar now={pct} />
      </div>
    );
  };

  const DistCard = ({ titleNode, items, totalSection }) => (
    <Card className="mb-3 shadow-sm">
      <Card.Body>
        <SectionHeader
          title={titleNode}
          right={
            <>
              <span className="me-1">Total:</span>
              <strong>{totalSection}</strong>
            </>
          }
        />
        {items.length === 0 && <div className="text-muted">Sem dados</div>}
        {items.map((it) => (
          <BarRow
            key={it.key}
            labelLeft={it.label}
            value={it.counter}
            total={totalSection}
          />
        ))}
      </Card.Body>
    </Card>
  );

  return (
    <div className="container-fluid" ref={pdfRef}>
      <Row className="align-items-center mb-3">
        <Col>
          <h4 className="text-secondary mb-0">
            <FormattedMessage
              id="indicator.candidatures.byPromoter"
              defaultMessage="Candidaturas por Dados do Representante"
            />
          </h4>
          <small className="text-muted">
            Os totais referem-se ao número de representantes (promotores activos) das candidaturas — uma candidatura pode ter mais do que um representante.
          </small>
        </Col>
        <Col className="text-end">
          <a
            href="#"
            onClick={(e) => {
              e.preventDefault();
              setShowExport(true);
            }}
            style={{ color: "#e5533d", textDecoration: "underline" }}
            data-hide-on-export="true"
          >
            <FormattedMessage
              id="indicator.exportar"
              defaultMessage="Exportar"
            />
          </a>
        </Col>
      </Row>

      <Accordion defaultActiveKey="0" alwaysOpen>
        <Accordion.Item eventKey="0">
          <Accordion.Header>Género e Grupo Etário</Accordion.Header>
          <Accordion.Body>
            <Row>
              <Col lg={6}>
                <DistCard
                  titleNode={
                    <FormattedMessage
                      id="indicator.gender.column"
                      defaultMessage="Género"
                    />
                  }
                  items={gendersView}
                  totalSection={sumBy(gendersView)}
                />
              </Col>
              <Col lg={6}>
                <DistCard
                  titleNode={
                    <FormattedMessage
                      id="indicator.ageGroup.column"
                      defaultMessage="Grupo Etário"
                    />
                  }
                  items={agesArray}
                  totalSection={totalAges}
                />
              </Col>
            </Row>
          </Accordion.Body>
        </Accordion.Item>

        <Accordion.Item eventKey="1">
          <Accordion.Header>
            Habilitação e Nível de Qualificação
          </Accordion.Header>
          <Accordion.Body>
            <Row>
              <Col lg={6}>
                <DistCard
                  titleNode={
                    <FormattedMessage
                      id="indicator.schoolQualification.column"
                      defaultMessage="Habilitação Escolar"
                    />
                  }
                  items={schoolsView}
                  totalSection={sumBy(schoolsView)}
                />
              </Col>
              <Col lg={6}>
                <DistCard
                  titleNode={
                    <FormattedMessage
                      id="indicator.qualificationLevel.column"
                      defaultMessage="Nível de Qualificação"
                    />
                  }
                  items={qualificationsView}
                  totalSection={sumBy(qualificationsView)}
                />
              </Col>
            </Row>
          </Accordion.Body>
        </Accordion.Item>
      </Accordion>

      <LastUpdate iso={lastUpdate} version={refreshVersion} className="mt-3" />

      <ExportDialog
        show={showExport}
        onHide={() => setShowExport(false)}
        options={[
          {
            onClick: handleExportExcelFinal,
            filename: "indicadores_representativos.xlsx",
          },
          {
            onClick: () => exportPdf("indicadores_representativos.pdf"),
            filename: "indicadores_representativos.pdf",
          },
        ]}
      />
    </div>
  );
}
